import type { KeyboardEventHandler, SetStateAction } from 'react'
import { useState, type FC } from 'react'

import type { ValueLabelPair } from '@skytech/ui'
import {
  Checkbox,
  createOption,
  InfoBox,
  Label,
  MultiSelect,
  Radio,
  RadioGroup,
  StyledText,
} from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import type { FieldProps } from 'formik'
import { Field, useFormikContext } from 'formik'
import type { ActionMeta } from 'react-select'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'
import type { LeasingSettings } from '@/resources/leasing'
import { validateEmail } from '@/util/formatters/text'

import { normalizeEmployeeFieldsOnDisable } from './helpers'
import { Timeline } from './Timeline'

import type { LeasingSettingsFormFields } from '.'

export const EndOfLeaseForm: FC<{
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
  errorMsg?: string
  lockFields?: string[]
  isDisabled?: boolean
  data?: LeasingSettings
  onPopupClose: () => void
}> = ({
  isLoading = false,
  isError = false,
  isSuccess = false,
  errorMsg,
  lockFields,
  isDisabled = false,
  data,
  onPopupClose,
}) => {
  const isThreeStepIT = useIsThreeStepIT()
  const translate = useTranslation()
  const { dirty, isValid, isSubmitting, values, setFieldValue } =
    useFormikContext<LeasingSettingsFormFields>()
  const [creatableValue, setCreatableValue] = useState<ValueLabelPair[]>(
    values.notificationEmails?.map(email => createOption(email)) || []
  )
  const [inputValue, setInputValue] = useState('')

  const handleKeyDown: KeyboardEventHandler = event => {
    if (!inputValue) return
    if (!validateEmail(inputValue)) return

    switch (event.key) {
      case 'Enter':
      case 'Tab': {
        const newOption = createOption(inputValue)
        const updatedOptions = [...creatableValue, newOption]
        const notificationEmails = updatedOptions.map(option => option.value)

        setCreatableValue(updatedOptions)
        setFieldValue('notificationEmails', notificationEmails)
        setInputValue('')
        event.preventDefault()
        break
      }
    }
  }

  const handleMultiSelectChange = (
    args: SetStateAction<ValueLabelPair[]>,
    actionMeta?: ActionMeta<ValueLabelPair>
  ) => {
    if (actionMeta) {
      switch (actionMeta.action) {
        case 'remove-value':
        case 'pop-value': {
          const updatedOptions = creatableValue.filter(
            option => option.value !== actionMeta.removedValue?.value
          )
          setFieldValue(
            'notificationEmails',
            updatedOptions.map(option => option.value)
          )
          break
        }
        case 'clear': {
          setFieldValue('notificationEmails', [])
          break
        }
        default:
          break
      }
    }
    setCreatableValue(args as ValueLabelPair[])
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <StyledText fontWeight="semibold" size="large" colorMode="gray">
          <Translation id="leasing-settings/manager" />
        </StyledText>
        <div className="mt-1 h-0.5 w-full bg-gray-300 opacity-50" />
      </div>

      <InfoBox>
        <Translation id="We will send an email to each recipient about leased assets reaching their end of lease time. All assets in use with 90 days or less remaining on their lease will be included in the email." />
      </InfoBox>

      <RadioGroup
        id="send-notifications"
        label={
          <Label>
            <Translation id="Notify manager about upcoming end of lease?" />
          </Label>
        }
        className="mb-4 ml-[5px]"
      >
        <Field name="sendEndOfLeaseNotification">
          {({ field, form }: FieldProps) => (
            <Radio
              capitalizeLabel
              id="yes"
              label={<Translation id="Yes" />}
              {...field}
              checked={field.value === true}
              onChange={() => {
                form.setFieldValue('sendEndOfLeaseNotification', true)
                form.setFieldTouched('notificationEmails', true)
              }}
            />
          )}
        </Field>

        <Field name="sendEndOfLeaseNotification">
          {({ field, form }: FieldProps) => (
            <Radio
              capitalizeLabel
              id="no"
              label={<Translation id="No" />}
              {...field}
              checked={field.value === false}
              onChange={() => form.setFieldValue('sendEndOfLeaseNotification', false)}
            />
          )}
        </Field>
      </RadioGroup>

      <Field name="notificationEmails">
        {({ field, form, meta }: FieldProps) => (
          <MultiSelect
            isCreatable
            id="notificationEmails"
            className="ml-[5px]"
            label={
              <>
                <Translation id="Notification Emails" />
                {form.values.sendEndOfLeaseNotification && '*'}
              </>
            }
            placeholder={translate(
              'Type in email address here and press enter to add to list of receivers'
            )}
            options={creatableValue}
            inputValue={inputValue}
            handleKeyDown={handleKeyDown}
            onInputChange={(newValue: SetStateAction<string>) => setInputValue(newValue)}
            touched={meta.touched}
            error={meta.error}
            marginBottom={16}
            {...field}
            onBlur={() => form.setFieldTouched(field.name, true)}
            onChange={handleMultiSelectChange}
          />
        )}
      </Field>

      <div className="mt-4 flex w-full flex-row items-center justify-between gap-2">
        <StyledText fontWeight="semibold" size="large" colorMode="gray">
          <Translation id="Employee" />
        </StyledText>
        <div className="mt-1 h-0.5 w-full bg-gray-300 opacity-50" />
      </div>

      <Field name="endOfLeaseIsActive">
        {({ field, meta, form }: FieldProps) => (
          <Checkbox
            id="endOfLeaseIsActive"
            checked={field.value}
            label={translate('Enable end-of-lease automation')}
            touched={meta.touched}
            error={meta.error}
            isDisabled={isDisabled}
            {...field}
            onChange={event => {
              form.setFieldValue(field.name, event.target.checked)
              if (!event.target.checked) {
                normalizeEmployeeFieldsOnDisable(isThreeStepIT, form, data)
              }
            }}
          />
        )}
      </Field>

      <Timeline lockFields={lockFields} isDisabled={isDisabled} />

      <footer>
        {isSuccess && (
          <InfoBox emphasis="success">
            <Translation id="End of lease settings successfully updated" />
          </InfoBox>
        )}
        {isError && (
          <InfoBox emphasis="error">
            {errorMsg ? (
              <Translation id={errorMsg} />
            ) : (
              <Translation id="Something went wrong when updating leasing settings. Please, try again later." />
            )}
          </InfoBox>
        )}
        <div className="mt-4 flex w-full flex-col justify-end gap-4 sm:flex-row">
          <Button
            onClick={onPopupClose}
            className="basis-1/4"
            disabled={isDisabled || isSubmitting || isLoading}
          >
            <Translation id="Cancel" />
          </Button>
          <Button
            type="submit"
            className="basis-1/4"
            variant="primary"
            isLoading={isLoading}
            disabled={isDisabled || !dirty || !isValid || isSubmitting || isLoading}
          >
            <Translation id="Save" />
          </Button>
        </div>
      </footer>
    </div>
  )
}
